import "./form.scss";

import ArrowNextIcon from "@frontend/images/icons/ArrowNextIcon";
import CrossIcon from "@frontend/images/icons/CrossIcon";
import ConfirmForm from "@frontend/images/ConfirmForm";
import ErrorCross from "@frontend/images/ErrorCross";
import { Link, useForm } from "@inertiajs/react";
import { useEffect, useState } from "react";
import useT from "@frontend/js/hooks/useT";

const FIELD_RULES = {
    fullName: {
        label: "form-full-name-label",
    },
    companyName: {
        label: "form-company-label",
    },
    email: {
        label: "form-email-label",
    },
    phone: {
        label: "form-phone-label",
    },
    message: {
        label: "form-message-label",
    },
    consent: {
        message: "validation-consent-required",
    },
};

const INITIAL_DATA = {
    fullName: "",
    companyName: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
};

const DRAWER_CLOSE_ANIMATION_MS = 460;

export default function Form({
    onSubmit,
    submitLabel,
    variant = "default",
    isOpen = true,
    onClose,
    title,
    source,
    sourceId,
}) {
    const { t } = useT("site");
    const formTitle = title || t("contact-form-title");
    const { data, setData, errors, setError, clearErrors, reset } =
        useForm(INITIAL_DATA);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitModalState, setSubmitModalState] = useState(null);
    const [isDrawerMounted, setIsDrawerMounted] = useState(
        variant === "drawer" ? isOpen : false,
    );
    const [isDrawerClosing, setIsDrawerClosing] = useState(false);

    const isSubmitModalOpen = submitModalState !== null;
    const isSubmitSuccessful = submitModalState === "success";
    const isDrawerVariant = variant === "drawer";
    const isDrawerVisible = isDrawerVariant && isOpen;

    useEffect(() => {
        if (!isDrawerVariant) {
            return undefined;
        }

        if (isDrawerVisible) {
            setIsDrawerMounted(true);
            setIsDrawerClosing(false);
            return undefined;
        }

        if (!isDrawerMounted) {
            return undefined;
        }

        setIsDrawerClosing(true);

        const timeoutId = window.setTimeout(() => {
            setIsDrawerClosing(false);
            setIsDrawerMounted(false);
        }, DRAWER_CLOSE_ANIMATION_MS);

        return () => window.clearTimeout(timeoutId);
    }, [isDrawerMounted, isDrawerVariant, isDrawerVisible]);

    function handleRequestClose() {
        if (isSubmitModalOpen) {
            return;
        }

        if (onClose) {
            onClose();
        }
    }

    function handleSubmitModalClose(event) {
        closeSubmitModal(event);

        if (isSubmitSuccessful && onClose) {
            onClose();
        }
    }

    function closeSubmitModal(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setSubmitModalState(null);
    }

    function handleSubmitResultButtonClick(event) {
        const shouldCloseDrawer = isSubmitSuccessful;

        closeSubmitModal(event);

        if (shouldCloseDrawer && onClose) {
            onClose();
        }
    }

    useEffect(() => {
        const shouldLockScroll =
            isSubmitModalOpen || (isDrawerVariant && isDrawerMounted);

        if (!shouldLockScroll) {
            return undefined;
        }

        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyPaddingRight = document.body.style.paddingRight;

        const htmlComputedStyle = window.getComputedStyle(
            document.documentElement,
        );
        const hasStableScrollbarGutter = (
            htmlComputedStyle.scrollbarGutter || ""
        ).includes("stable");
        const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;
        const computedBodyPaddingRight =
            Number.parseFloat(
                window.getComputedStyle(document.body).paddingRight,
            ) || 0;

        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        if (!hasStableScrollbarGutter && scrollbarWidth > 0) {
            document.body.style.paddingRight = `${computedBodyPaddingRight + scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.paddingRight = previousBodyPaddingRight;
        };
    }, [isDrawerMounted, isDrawerVariant, isSubmitModalOpen]);

    function handleChange(event) {
        const { name, value, type, checked } = event.target;

        setData(name, type === "checkbox" ? checked : value);
        clearErrors(name);
        setIsSubmitted(false);
    }

    async function handleSubmit(event) {
        event.preventDefault();

        const normalizedData = Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                typeof value === "string" ? value.trim() : value,
            ]),
        );

        const pageProps = window.page?.props || {};
        const locale = pageProps.currentLocale || "ro";

        try {
            let isSendSuccessful = !onSubmit;

            if (onSubmit) {
                const submitResult = await onSubmit(normalizedData, {
                    reset,
                    clearErrors,
                    setError,
                    locale,
                    source,
                    sourceId,
                });
                // validationErrors flag means validation errors - don't show any modal
                if (submitResult?.validationErrors) {
                    setIsSubmitted(false);
                    return;
                }
                isSendSuccessful = submitResult === true;
            }

            if (!isSendSuccessful) {
                setSubmitModalState("error");
                setIsSubmitted(false);
                return;
            }

            reset();
            clearErrors();
            setIsSubmitted(true);
            setSubmitModalState("success");
        } catch {
            setSubmitModalState("error");
            setIsSubmitted(false);
        }
    }

    function getFieldClass(fieldName) {
        return errors[fieldName]
            ? "form__grid__field form__grid__field--invalid"
            : "form__grid__field";
    }

    const formMarkup = (
        <form
            className={isDrawerVariant ? "form form--drawer" : "form"}
            onSubmit={handleSubmit}
            noValidate
        >
            <div className="form__grid">
                <label className={getFieldClass("fullName")}>
                    <span>{t("form-full-name-label")}</span>
                    <input
                        type="text"
                        name="fullName"
                        value={data.fullName}
                        onChange={handleChange}
                        placeholder={t("form-full-name-placeholder")}
                        autoComplete="name"
                    />
                    {errors.fullName ? (
                        <div className="form__grid__field__error">
                            {errors.fullName}
                        </div>
                    ) : null}
                </label>

                <label className={getFieldClass("companyName")}>
                    <span>{t("form-company-label")}</span>
                    <input
                        type="text"
                        name="companyName"
                        value={data.companyName}
                        onChange={handleChange}
                        placeholder={t("form-company-placeholder")}
                        autoComplete="organization"
                    />
                    {errors.companyName ? (
                        <div className="form__grid__field__error">
                            {errors.companyName}
                        </div>
                    ) : null}
                </label>

                <label className={getFieldClass("email")}>
                    <span>{t("form-email-label")}</span>
                    <input
                        type="email"
                        name="email"
                        value={data.email}
                        onChange={handleChange}
                        placeholder={t("form-email-placeholder")}
                        autoComplete="email"
                    />
                    {errors.email ? (
                        <div className="form__grid__field__error">
                            {errors.email}
                        </div>
                    ) : null}
                </label>

                <label className={getFieldClass("phone")}>
                    <span>{t("form-phone-label")}</span>
                    <input
                        type="tel"
                        name="phone"
                        value={data.phone}
                        onChange={handleChange}
                        placeholder={t("form-phone-placeholder")}
                        autoComplete="tel"
                    />
                    {errors.phone ? (
                        <div className="form__grid__field__error">
                            {errors.phone}
                        </div>
                    ) : null}
                </label>

                <label className={getFieldClass("message")}>
                    <span>{t("form-message-label")}</span>
                    <textarea
                        name="message"
                        value={data.message}
                        onChange={handleChange}
                        placeholder={t("form-message-placeholder")}
                        rows={4}
                    />
                    {errors.message ? (
                        <div className="form__grid__field__error">
                            {errors.message}
                        </div>
                    ) : null}
                </label>
            </div>

            <div className="form__footer">
                <label
                    className={
                        errors.consent
                            ? "form__footer__checkbox form__footer__checkbox--invalid"
                            : "form__footer__checkbox"
                    }
                >
                    <input
                        type="checkbox"
                        name="consent"
                        checked={data.consent}
                        onChange={handleChange}
                    />
                    <div className="form__footer__checkbox__content">
                        <p
                            className="form__footer__checkbox__text"
                            dangerouslySetInnerHTML={{
                                __html: t("form-consent-text"),
                            }}
                        ></p>
                        {errors.consent ? (
                            <div className="form__footer__checkbox__error">
                                {errors.consent}
                            </div>
                        ) : null}
                    </div>
                </label>
                <button type="submit">
                    {submitLabel || t("form-submit-button")} <ArrowNextIcon />
                </button>
            </div>
        </form>
    );

    return (
        <>
            {isDrawerVariant ? (
                isDrawerMounted ? (
                    <div
                        className={
                            isDrawerClosing
                                ? "form__drawer form__drawer--closing"
                                : "form__drawer"
                        }
                        role="dialog"
                        aria-modal="true"
                        aria-label={formTitle}
                    >
                        <button
                            type="button"
                            className="form__drawer__overlay"
                            onClick={(event) => {
                                if (isSubmitModalOpen) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    return;
                                }

                                handleRequestClose();
                            }}
                            aria-label="Închide fereastra"
                        />

                        <div className="form__drawer__panel">
                            <button
                                type="button"
                                className="form__drawer__panel__close"
                                onClick={handleRequestClose}
                                aria-label="Închide"
                            >
                                <CrossIcon />
                            </button>

                            <h2>{formTitle}</h2>
                            {formMarkup}
                        </div>
                    </div>
                ) : null
            ) : (
                formMarkup
            )}

            {isSubmitModalOpen ? (
                <div
                    className="form__submitError"
                    role="dialog"
                    aria-modal="true"
                    aria-label={
                        isSubmitSuccessful
                            ? "Trimitere reușită"
                            : "Eroare la trimitere"
                    }
                >
                    <button
                        type="button"
                        className="form__submitError__overlay"
                        onClick={closeSubmitModal}
                        aria-label="Închide fereastra"
                    />

                    <div
                        className={
                            isSubmitSuccessful
                                ? "form__submitError__panel form__submitError__panel--success"
                                : "form__submitError__panel"
                        }
                    >
                        <button
                            type="button"
                            className="form__submitError__panel__close"
                            onClick={handleSubmitModalClose}
                            aria-label="Închide"
                        >
                            <CrossIcon />
                        </button>

                        <div className="form__submitError__panel__content">
                            <h2>
                                {isSubmitSuccessful
                                    ? t("form-success-title")
                                    : t("form-error-title")}
                            </h2>
                            <p>
                                {isSubmitSuccessful
                                    ? t("form-success-message")
                                    : t("form-error-message")}
                            </p>
                            <button
                                type="button"
                                className={
                                    isSubmitSuccessful
                                        ? "form__submitError__panel__content__button form__submitError__panel__content__button--success"
                                        : "form__submitError__panel__content__button"
                                }
                                onClick={handleSubmitResultButtonClick}
                            >
                                {isSubmitSuccessful
                                    ? t("form-success-button")
                                    : t("form-error-button")}
                            </button>
                        </div>

                        {isSubmitSuccessful ? (
                            <ConfirmForm className="form__submitError__panel__illustration" />
                        ) : (
                            <ErrorCross className="form__submitError__panel__illustration" />
                        )}
                    </div>
                </div>
            ) : null}
        </>
    );
}
